import React, { useState } from "react";
import classNames from "classnames";
import { Icomoon } from "../../brand";
import { IconListItem, Dialog } from "../../layout";
import { ItinerarySection } from "..";
import { truncate } from "@travellocal/utils/stringHelpers";
import "./ItineraryServices.scss";

interface Props {

  /**
   * Included services for this itinerary.
   * 
   * N.B. This is stored in the backend as a string blob, but most LTOs input as a list with newlines.
   * It would be good to make this format stronger.
   */
  includedServices: string;

  /**
   * Excluded services from this itinerary.
   * 
   * N.B. This is stored in the backend as a string blob, but most LTOs input as a list with newlines.
   * It would be good to make this format stronger.
   */
  excludedServices: string;

  className?: string;
}

export const servicesStringToArray = (services: string): string[] => {
  return (services ?? "").split("\n")
    .map(line => line.trim())
    .map(line => line.replace(/^(-|\*)+\W*/, "")) // Trim any bullet-like characters
    .map(line => truncate(line, 100))
    .filter(line => line.length > 0)
};

/**
 * Component to display included and excluded services on an itinerary.
 * 
 * @status development
 */
export const ItineraryServices: React.FC<Props> = ({ className, includedServices, excludedServices }) => {
  const inclusions = servicesStringToArray(includedServices).slice(0, 3);
  const exclusions = servicesStringToArray(excludedServices).slice(0, 3);

  const [isInclusionsOpen, setIsInclusionsOpen] = useState(false);
  const [isExclusionsOpen, setIsExclusionsOpen] = useState(false);

  return (
    <ItinerarySection
      className={classNames("itinerary-services", className)}
      icomoon={Icomoon.checkmark3}
      title="What's included?"
    >
      <Dialog
        title="Included in your itinerary"
        ariaLabel="Included services for your itinerary"
        className="itinerary-services__dialog itinerary-services__dialog--inc"
        isOpen={isInclusionsOpen}
        onClose={() => setIsInclusionsOpen(false)}
        hasCloseButton
      >
        <p>{includedServices}</p>
      </Dialog>

      <Dialog
        title="Excluded from your itinerary"
        ariaLabel="Excluded services from your itinerary"
        className="itinerary-services__dialog itinerary-services__dialog--exc"
        isOpen={isExclusionsOpen}
        onClose={() => setIsExclusionsOpen(false)}
        hasCloseButton
      >
        <p>{excludedServices}</p>
      </Dialog>

      <div className="columns">
        <div className="column is-half is-flex itinerary-services__services">
          <div>
            {inclusions.map((line, index) => (
              <IconListItem
                key={index}
                icomoon={Icomoon.checkmark3}
                className="itinerary-services__service itinerary-services__service--inc"
              >
                <p className="is-marginless is-block">
                  {line}
                </p>
              </IconListItem>
            ))}
          </div>

          <button
            type="button"
            className="secondary-button itinerary-services__btn itinerary-services__btn--inc"
            onClick={() => setIsInclusionsOpen(true)}>
            <span>See all inclusions</span>
          </button>
        </div>

        <div className="column is-half is-flex itinerary-services__services">
          <div>
            {exclusions.map((line, index) => (
              <IconListItem
                key={index}
                icomoon={Icomoon.cross2}
                className="itinerary-services__service itinerary-services__service--exc"
              >
                <p className="is-marginless is-block">
                  {line}
                </p>
              </IconListItem>
            ))}
          </div>

          <button
            type="button"
            className="secondary-button itinerary-services__btn itinerary-services__btn--exc"
            onClick={() => setIsExclusionsOpen(true)}
          >
            <span>See all exclusions</span>
          </button>
        </div>
      </div>

      <p className="is-marginless">
        All services are subject to availability, unless otherwise specified.
      </p>
    </ItinerarySection>
  );
}
